import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";
import { PillarSection, TransparencyNote } from "@/components/PageShell";
import { TableOfContents } from "@/components/TableOfContents";
import { TravelSection } from "@/components/TravelSection";
import { Reveal } from "@/components/Reveal";

// Visuels FACOW360 — Cuisine Vivante
// Intro "recherche & fréquence" (Alchimie Digitale)
const IMG_ANALYSE_CELL = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/t6kx4v0h_Sans%20titre5.jpg";
const IMG_ALCHIMIE = "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/wshujfu7_Sans%20titrek.jpg";

// Galerie de 5 plats originaux du chef

const DISH_PHOTOS = [
  {
    src: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/bwo0s262_photos%20pour%20le%20site%201.jpg",
    title_fr: "Cannelloni de courgette crue, sauce méditerranéenne",
    title_en: "Raw zucchini cannelloni, Mediterranean sauce",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/i3lem5hj_photos%20pour%20le%20site%202.jpg",
    title_fr: "Champignons farcis aux graines, fleurs comestibles",
    title_en: "Stuffed mushrooms with seeds and edible flowers",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/jgd8z7uv_photos%20pour%20le%20site%203.jpg",
    title_fr: "Maki de nori cru, légumes croquants & germes",
    title_en: "Raw nori maki, crunchy vegetables & sprouts",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/nwxxn5h5_photos%20pour%20le%20site%204.jpg",
    title_fr: "Carpaccio de betterave, sauce rose & micropousses",
    title_en: "Beetroot carpaccio, rose sauce & microgreens",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_ai-vivant/artifacts/zxbcu9ib_photos%20pour%20le%20site%205.jpg",
    title_fr: "Rouleaux de printemps cristal, graines & coriandre",
    title_en: "Crystal spring rolls, seeds & coriander",
  },
];

const Gallery = ({ isFr }) => (
  <section className="mt-12 scroll-mt-28" id="creations" data-testid="cuisine-section-creations">
    <h2 className="font-display text-2xl md:text-3xl text-cyan-glow mb-5 leading-tight tracking-tight">
      {isFr ? "Mes créations" : "My creations"}
    </h2>
    <p className="text-slate-300 leading-relaxed text-base max-w-4xl mb-8">
      {isFr
        ? "Voici quelques-unes de mes assiettes — toutes pensées, dressées et photographiées dans mon univers culinaire personnel. Une mise en scène simple et noble du vivant."
        : "Here are a few of my plates — all conceived, plated and photographed within my own culinary world. A simple, noble staging of the living."}
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {DISH_PHOTOS.map((p, i) => (
        <Reveal key={i} direction="up" delay={i * 0.1} duration={0.85} amount={0.15}>
          <figure
            className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/20 bg-[#0A1128]/60 hover:border-[#D4AF37]/60 transition-colors h-full"
            data-testid={`dish-photo-${i}`}
          >
            <div className="aspect-square overflow-hidden">
              <img
                src={p.src}
                alt={isFr ? p.title_fr : p.title_en}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/85 to-transparent">
              <figcaption className="font-display text-sm md:text-base text-amber-glow leading-snug">
                {isFr ? p.title_fr : p.title_en}
              </figcaption>
            </div>
          </figure>
        </Reveal>
      ))}
    </div>
    <p className="mt-6 text-xs text-slate-500 italic">
      {isFr
        ? "Photographies originales — créations personnelles. Reproduction interdite sans autorisation."
        : "Original photographs — personal creations. Reproduction prohibited without authorisation."}
    </p>
  </section>
);

export default function CuisineVivante() {
  const { locale, t } = useLang();
  const isFr = locale === "fr";

  const tocItems = isFr
    ? [
        { id: "why", label: "Pourquoi la Cuisine Vivante ?" },
        { id: "creations", label: "Mes créations" },
        { id: "blood", label: "1. Le système sanguin" },
        { id: "ayurveda", label: "2. L'Ayurvéda" },
        { id: "naturopathy", label: "3. Naturopathie & émonctoires" },
        { id: "chrono", label: "4. La chronobiologie" },
        { id: "fasting", label: "5. Le jeûne" },
        { id: "grades", label: "Les 5 grades de la pratique" },
        { id: "deplacements", label: "Interventions & déplacements" },
      ]
    : [
        { id: "why", label: "Why Living Cuisine?" },
        { id: "creations", label: "My creations" },
        { id: "blood", label: "1. The blood system" },
        { id: "ayurveda", label: "2. Ayurveda" },
        { id: "naturopathy", label: "3. Naturopathy & emunctories" },
        { id: "chrono", label: "4. Chronobiology" },
        { id: "fasting", label: "5. Fasting" },
        { id: "grades", label: "The 5 grades of practice" },
        { id: "deplacements", label: "Bespoke interventions & travel" },
      ];

  return (
    <main className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-10" data-testid="page-cuisine">
      <div className="flex">
        <div className="flex-1 min-w-0">
          <p className="label-overline mb-4">FACOW360 / Pillar</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-glow leading-tight mb-6 tracking-tight">
            {isFr ? "Cuisine Vivante : les 5 piliers de la vitalité" : "Living Cuisine: the 5 pillars of vitality"}
          </h1>
          <p className="text-base lg:text-lg text-slate-300 max-w-3xl leading-relaxed mb-12">
            {isFr
              ? "L'alimentation ne se résume pas à remplir un estomac : c'est la transmission directe d'une information et d'une énergie de la nature vers nos cellules. La Cuisine Vivante est une approche vibrante qui remet la vitalité au centre de notre quotidien."
              : "Food is not about filling a stomach: it is the direct transmission of information and energy from nature to our cells. Living Cuisine is a vibrant approach that puts vitality back at the heart of daily life."}
          </p>

          <PillarSection
            id="why"
            heading={isFr ? "Pourquoi la Cuisine Vivante ?" : "Why Living Cuisine?"}
            testId="cuisine-section-why"
          >
            <p>
              {isFr
                ? "L'alimentation moderne épuise notre capital enzymatique. À l'inverse, comprendre son corps et lui offrir une nourriture vivante permet de rediriger l'énergie digestive vers la régénération cellulaire et la clarté mentale."
                : "Modern eating depletes our enzymatic capital. Conversely, understanding the body and offering it living food allows digestive energy to be redirected towards cellular regeneration and mental clarity."}
            </p>
            <p>
              {isFr
                ? "Ces cinq fondements permettent de nourrir son corps de manière holistique."
                : "These five foundations allow you to nourish the body holistically."}
            </p>
          </PillarSection>

          {/* Bloc d'introduction visuel — Recherche & Fréquence */}
          <section className="mt-12" data-testid="cuisine-section-research">
            <Reveal direction="up" amount={0.2}>
              <p className="label-overline mb-3">
                {isFr ? "— Recherche & Fréquence" : "— Research & Frequency"}
              </p>
              <h2 className="font-display text-2xl md:text-3xl text-cyan-glow mb-4 leading-tight tracking-tight">
                {isFr ? "L'aliment comme information vivante" : "Food as living information"}
              </h2>
              <p className="text-slate-300 leading-relaxed text-base max-w-4xl mb-8">
                {isFr
                  ? "Avant le dressage de l'assiette, il y a la lecture vibratoire de chaque ingrédient — graines germées, jus pressés, oléagineux. Mesurer la fréquence cellulaire, cartographier l'ordre vivant : c'est le laboratoire silencieux qui précède chaque création."
                  : "Before the plate is composed, there is the vibrational reading of every ingredient — sprouted seeds, pressed juices, oilseeds. Measuring the cellular frequency and mapping the living order: this is the silent laboratory preceding every creation."}
              </p>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Reveal direction="up" delay={0.0} duration={0.95} amount={0.2}>
                <figure
                  className="group relative overflow-hidden rounded-2xl border border-[#00F0FF]/25 bg-[#0F1A3A] aspect-[4/5]"
                  data-testid="cuisine-research-1"
                >
                  <img
                    src={IMG_ANALYSE_CELL}
                    alt={isFr
                      ? "Analyse cellulaire FACOW360 — lecture vibratoire des graines germées et de l'ordre cellulaire"
                      : "FACOW360 cellular analysis — vibrational reading of sprouted seeds and cellular order"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/30 to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 right-0 px-5 py-4">
                    <span className="inline-block px-3 py-1.5 rounded-full bg-[#0F1A3A]/85 backdrop-blur-sm border border-[#00F0FF]/40 text-[#00F0FF] text-[10px] font-mono-tech uppercase tracking-[0.25em]">
                      {isFr ? "01 · Analyse cellulaire" : "01 · Cellular analysis"}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>

              <Reveal direction="up" delay={0.15} duration={0.95} amount={0.2}>
                <figure
                  className="group relative overflow-hidden rounded-2xl border border-[#D4AF37]/30 bg-[#0F1A3A] aspect-[4/5]"
                  data-testid="cuisine-research-2"
                >
                  <img
                    src={IMG_ALCHIMIE}
                    alt={isFr
                      ? "Alchimie Digitale FACOW360 — cartographie de fréquence et activation cellulaire 528 Hz"
                      : "FACOW360 Digital Alchemy — frequency map and cellular activation 528 Hz"}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                  />
                  <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0F1A3A] via-[#0F1A3A]/30 to-transparent" />
                  <figcaption className="absolute bottom-0 left-0 right-0 px-5 py-4">
                    <span className="inline-block px-3 py-1.5 rounded-full bg-[#0F1A3A]/85 backdrop-blur-sm border border-[#D4AF37]/40 text-[#D4AF37] text-[10px] font-mono-tech uppercase tracking-[0.25em]">
                      {isFr ? "02 · Alchimie Digitale" : "02 · Digital Alchemy"}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            </div>

            <p className="mt-5 text-xs text-slate-500 italic">
              {isFr
                ? "Visuels originaux FACOW360 — illustration du protocole de lecture vibratoire. Le résultat concret est ensuite dressé dans la galerie ci-dessous."
                : "Original FACOW360 visuals — illustrating the vibrational reading protocol. The concrete result is then plated in the gallery below."}
            </p>
          </section>

          <Gallery isFr={isFr} />

          <PillarSection
            id="blood"
            heading={isFr ? "1. Le système sanguin" : "1. The blood system"}
            testId="cuisine-section-blood"
          >
            <p>
              {isFr
                ? "Chaque métabolisme est unique. Comprendre l'impact de l'alimentation sur notre système sanguin permet de choisir les aliments les mieux assimilés, évitant ainsi les réactions inflammatoires et la fatigue digestive."
                : "Every metabolism is unique. Understanding food's impact on the blood system makes it possible to select the best-assimilated foods, avoiding inflammatory reactions and digestive fatigue."}
            </p>
          </PillarSection>

          <PillarSection
            id="ayurveda"
            heading={isFr ? "2. L'Ayurvéda" : "2. Ayurveda"}
            testId="cuisine-section-ayurveda"
          >
            <p>
              {isFr
                ? "Cette science de la vie nous apprend à manger en fonction de notre constitution profonde et des saisons. Elle utilise la magie des épices et l'équilibre des températures pour maintenir notre feu digestif (Agni) toujours optimal."
                : "This science of life teaches us to eat according to our deep constitution and the seasons. It uses the magic of spices and the balance of temperatures to keep our digestive fire (Agni) always optimal."}
            </p>
          </PillarSection>

          <PillarSection
            id="naturopathy"
            heading={isFr ? "3. La naturopathie et les émonctoires" : "3. Naturopathy and the emunctories"}
            testId="cuisine-section-naturopathy"
          >
            <p>
              {isFr
                ? "Cette approche vise à soutenir les capacités naturelles d'auto-régulation du corps en privilégiant des aliments hypotoxiques et en favorisant le bon fonctionnement de nos organes d'élimination (les émonctoires), pour un organisme purifié."
                : "This approach supports the body's natural self-regulation capacities by favouring low-toxin foods and promoting the proper functioning of our elimination organs (the emunctories), for a purified organism."}
            </p>
          </PillarSection>

          <PillarSection
            id="chrono"
            heading={isFr ? "4. La chronobiologie" : "4. Chronobiology"}
            testId="cuisine-section-chrono"
          >
            <p>
              {isFr
                ? "Le respect de notre horloge interne. Il ne s'agit pas seulement de choisir ce que l'on mange, mais de savoir quand le manger pour respecter les rythmes naturels de sécrétion hormonale et enzymatique."
                : "Respecting our internal clock. It's not just about choosing what to eat but knowing when to eat it, in order to honour the natural rhythms of hormonal and enzymatic secretion."}
            </p>
          </PillarSection>

          <PillarSection
            id="fasting"
            heading={isFr ? "5. Le jeûne" : "5. Fasting"}
            testId="cuisine-section-fasting"
          >
            <p>
              {isFr
                ? "Le repos réparateur. Qu'il soit intermittent ou prolongé, le jeûne offre une pause vitale au système digestif. Ce temps de repos déclenche des processus profonds de nettoyage et de régénération cellulaire (l'autophagie)."
                : "Restorative rest. Whether intermittent or prolonged, fasting offers a vital pause to the digestive system. This rest triggers deep processes of cellular cleansing and regeneration (autophagy)."}
            </p>
          </PillarSection>

          <PillarSection
            id="grades"
            heading={isFr ? "Les 5 grades de la pratique" : "The 5 grades of practice"}
            testId="cuisine-section-grades"
          >
            <p>
              {isFr
                ? "Pour mettre en œuvre ces piliers, la Cuisine Vivante s'articule autour de cinq niveaux :"
                : "To put these pillars into practice, Living Cuisine unfolds across five levels:"}
            </p>
            <ul className="list-none space-y-3 pl-0">
              <li>
                — <strong className="text-amber-glow">{isFr ? "Grade 1 — Le végétal brut" : "Grade 1 — Raw plant matter"}</strong>{" "}
                {isFr
                  ? ": aliments non transformés et cuissons très douces."
                  : ": unprocessed foods and very gentle cooking."}
              </li>
              <li>
                — <strong className="text-amber-glow">{isFr ? "Grade 2 — La germination" : "Grade 2 — Germination"}</strong>{" "}
                {isFr
                  ? ": intégrer les graines germées pour démultiplier la force vitale."
                  : ": integrating sprouted seeds to multiply vital force."}
              </li>
              <li>
                — <strong className="text-amber-glow">{isFr ? "Grade 3 — Les super-aliments" : "Grade 3 — Super-foods"}</strong>{" "}
                {isFr
                  ? ": utiliser des plantes d'une richesse exceptionnelle (maca, chanvre) pour soutenir le système nerveux."
                  : ": using exceptionally rich plants (maca, hemp) to support the nervous system."}
              </li>
              <li>
                — <strong className="text-amber-glow">{isFr ? "Grade 4 — L'hydratation cellulaire" : "Grade 4 — Cellular hydration"}</strong>{" "}
                {isFr
                  ? ": jus extraits à froid pour une assimilation directe des minéraux."
                  : ": cold-pressed juices for direct mineral assimilation."}
              </li>
              <li>
                — <strong className="text-amber-glow">{isFr ? "Grade 5 — L'art culinaire" : "Grade 5 — Culinary art"}</strong>{" "}
                {isFr
                  ? ": l'équilibre et le plaisir par le mariage des textures et des saveurs."
                  : ": balance and pleasure through the marriage of textures and flavours."}
              </li>
            </ul>
          </PillarSection>

          <TransparencyNote>
            {isFr
              ? "Mes conseils culinaires s'appuient sur mon expérience de cuisinier et mon approche holistique. Ils ne constituent pas un bilan diététique ou médical. Je mets en œuvre des moyens techniques et sensibles, mais je ne peux garantir de résultats sur des pathologies qui relèvent de la médecine."
              : "My culinary advice draws on my chef's experience and my holistic approach. It does not constitute a dietary or medical assessment. I deploy technical and sensitive means, but I cannot guarantee results on pathologies that fall under medicine."}
          </TransparencyNote>

          <TravelSection pillar="cuisine" />

          <div className="mt-12 flex flex-wrap gap-3">
            <Link to="/bilan" className="btn-gold" data-testid="cuisine-cta-bilan">
              {isFr ? "Commencer mon Bilan (19 €)" : "Start my Assessment (€19)"}
            </Link>
            <Link to="/contact" className="btn-primary" data-testid="cuisine-cta-contact">
              {t("nav.contact")}
            </Link>
          </div>
        </div>

        <TableOfContents items={tocItems} title={isFr ? "Sommaire" : "Contents"} />
      </div>
    </main>
  );
}
